import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
// Note: We are not using the generic AudioButton here anymore to ensure integration with the main player
import { getImageUrl, getAudioUrl } from '../config';
import * as FiIcons from 'react-icons/fi';

const { FiHeadphones, FiVolume2, FiPlay, FiPause, FiMic, FiMusic, FiUsers, FiBook, FiHeart, FiStar, FiAward, FiClock, FiTrendingUp, FiRepeat, FiSkipBack, FiSkipForward, FiSettings, FiDownload, FiShare2, FiChevronRight, FiChevronLeft, FiSearch, FiSun, FiMoon, FiSunrise, FiSunset, FiVolumeX } = FiIcons;

const AudioGallery = ({ updateProgress, setCurrentSection }) => {
  const [activeCategory, setActiveCategory] = useState('greetings');
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  
  // Ref to hold the Audio object instance
  const audioRef = useRef(null);

  useEffect(() => {
    setCurrentSection('audio');
    updateProgress('audio');
    
    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Update volume and speed when they change
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [volume, playbackSpeed]);

  const audioCategories = {
    greetings: {
      title: 'Greetings & Politeness',
      description: 'Essential daily phrases for social interaction',
      icon: FiHeart,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      items: [
        {
          id: 'selam',
          amharic: 'ሰላም',
          transliteration: 'selam',
          english: 'Hello / Peace',
          context: 'Universal greeting',
          audio: 'selam',
          image: getImageUrl('selam.jpg'),
          category: 'Basic Greeting',
          difficulty: 'Beginner',
          culturalNote: 'Used in all contexts, from casual to formal',
          examples: ['ሰላም! እንዴት ነሽ?', 'ሰላም እንደምን አሉ?'],
          emoji: '👋'
        },
        {
          id: 'endet-newot',
          amharic: 'እንዴት ነዎት?',
          transliteration: 'endet newot?',
          english: 'How are you? (formal)',
          context: 'Respectful inquiry to multiple people',
          audio: 'endet-newot',
          image: getImageUrl('endet-newot.jpg'),
          category: 'Formal Inquiry',
          difficulty: 'Intermediate',
          culturalNote: 'Shows respect when addressing groups or elders',
          examples: ['እርሶ እንዴት ነዎት?', 'እኔ ደህና ነኝ'],
          emoji: '🤝'
        },
        {
          id: 'dehna-negn',
          amharic: 'ደህና ነኝ',
          transliteration: 'dehna negn',
          english: 'I am well',
          context: 'Standard response to greetings',
          audio: 'dehna-negn',
          image: getImageUrl('dehna-negn.jpg'),
          category: 'Response',
          difficulty: 'Beginner',
          culturalNote: 'Common positive response in Ethiopian culture',
          examples: ['ደህና ነኝ, ይመስገን!', 'እኛ ደህና ነን'],
          emoji: '😊'
        },
        {
          id: 'enkwan-dehna',
          amharic: 'እንኳን ደህና መጡ',
          transliteration: 'enkwan dehna metu',
          english: 'Welcome (formal)',
          context: 'Honoring guests with respect',
          audio: 'enkwan-dehna',
          image: getImageUrl('enkwan-dehna.jpg'),
          category: 'Hospitality',
          difficulty: 'Intermediate',
          culturalNote: 'Essential phrase for Ethiopian hospitality culture',
          examples: ['እንኳን ደህና መጡ', 'እንኳን ደህና መጣሽ!'],
          emoji: '🏠'
        },
        {
          id: 'endet-aderu',
          amharic: 'እንዴት አደሩ',
          transliteration: 'endet aderu',
          english: 'Good morning (polite)',
          context: 'Morning greeting',
          audio: 'endet-aderu',
          image: getImageUrl('endet-aderu.jpg'),
          category: 'Time-based',
          difficulty: 'Beginner',
          culturalNote: 'Literally "How did you spend the night?" addressed to elders/groups.',
          examples: ['እንዴት አደሩ', 'ደህና አደሩ?'],
          emoji: '🌅'
        },
        {
          id: 'melkam-ken',
          amharic: 'መልካም ቀን',
          transliteration: 'melkam ken',
          english: 'Have a good day',
          context: 'Parting wish',
          audio: 'melkam-ken',
          image: getImageUrl('melkam-ken.jpg'),
          category: 'Well Wishes',
          difficulty: 'Beginner',
          culturalNote: 'A common blessing-like phrase used when parting ways during the day.',
          examples: ['መልካም ቀን', 'ለእርስዎም መልካም ቀን ይሁንልዎ'],
          emoji: '☀️'
        }
      ]
    },
    family: {
      title: 'Family Terms',
      description: 'Essential vocabulary for family relationships',
      icon: FiUsers,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      items: [
        {
          id: 'abat',
          amharic: 'አባት',
          transliteration: 'abat',
          english: 'Father',
          context: 'Family respect and hierarchy',
          audio: 'abat',
          image: getImageUrl('father.jpg'),
          category: 'Immediate Family',
          difficulty: 'Beginner',
          culturalNote: 'Father holds respected position in Ethiopian family',
          examples: ['ሰላም አባቴ', 'አባቴ ክብር ይስጥልኝ'],
          emoji: '👨'
        },
        {
          id: 'enat',
          amharic: 'እናት',
          transliteration: 'enat',
          english: 'Mother',
          context: 'Maternal respect and care',
          audio: 'enat',
          image: getImageUrl('mother.jpg'),
          category: 'Immediate Family',
          difficulty: 'Beginner',
          culturalNote: 'Mother is central to Ethiopian family life',
          examples: ['እናቴ እንዴት ናታ?'],
          emoji: '👩'
        },
        {
          id: 'beteseb',
          amharic: 'ቤተሰብ',
          transliteration: 'beteseb',
          english: 'Family',
          context: 'Collective family identity',
          audio: 'beteseb',
          image: getImageUrl('beteseb.jpg'),
          category: 'Collective Terms',
          difficulty: 'Intermediate',
          culturalNote: 'Emphasizes family unity over individual identity',
          examples: ['የእኛ ቤተሰብ ትልቅ ነው', 'ቤተሰብን ይደግፉ'],
          emoji: '👨‍👩‍👧‍👦'
        },
        {
          id: 'lij',
          amharic: 'ልጅ',
          transliteration: 'lij',
          english: 'Child',
          context: 'Family hierarchy and love',
          audio: 'lij',
          image: getImageUrl('boy.jpg'),
          category: 'Family Members',
          difficulty: 'Beginner',
          culturalNote: 'Children are cherished in Ethiopian culture',
          examples: ['ልጄ ደህና ነው', 'ልጆች ተማሩ'],
          emoji: '👶'
        },
        {
          id: 'wondim',
          amharic: 'ወንድም',
          transliteration: 'wondim',
          english: 'Brother',
          context: 'Sibling bond',
          audio: 'wondim',
          image: getImageUrl('wondim.jpg'),
          category: 'Siblings',
          difficulty: 'Beginner',
          culturalNote: 'Used for biological brothers and close/respected male friends.',
          examples: ['ወንድሜ ነው', 'ሰላም ወንድም'],
          emoji: '👦'
        },
        {
          id: 'ehit',
          amharic: 'እህት',
          transliteration: 'ehit',
          english: 'Sister',
          context: 'Sibling bond',
          audio: 'ehit',
          image: getImageUrl('ehit.jpg'),
          category: 'Siblings',
          difficulty: 'Beginner',
          culturalNote: 'Used for biological sisters and close/respected female friends.',
          examples: ['እህቴ ናት', 'የእኔ እህት'],
          emoji: '👧'
        }
      ]
    },
    pronouns: {
      title: 'Key Pronouns',
      description: 'Master essential pronouns for social communication',
      icon: FiUsers,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      items: [
        {
          id: 'ene',
          amharic: 'እኔ',
          transliteration: 'ene',
          english: 'I',
          context: 'Individual identity',
          audio: 'ene',
          image: getImageUrl('pointing-self.jpg'),
          category: 'Personal Pronouns',
          difficulty: 'Beginner',
          culturalNote: 'Used less frequently than "we" in collectivist contexts',
          examples: ['እኔ እመጣለሁ', 'እኔ ደህና ነኝ'],
          emoji: '👤'
        },
        {
          id: 'egna',
          amharic: 'እኛ',
          transliteration: 'egna',
          english: 'We',
          context: 'Collective identity and humility',
          audio: 'egna',
          image: getImageUrl('group-we.jpg'),
          category: 'Collective Pronouns',
          difficulty: 'Beginner',
          culturalNote: 'Reflects Ethiopian value of community over individual',
          examples: ['እኛ እንሰራለን', 'እኛ ደህና ነን'],
          emoji: '👥'
        },
        {
          id: 'erswo',
          amharic: 'እርስዎ',
          transliteration: 'erswo',
          english: 'You (formal)',
          context: 'Respectful address to elders',
          audio: 'erswo',
          image: getImageUrl('erswo.jpg'),
          category: 'Honorifics',
          difficulty: 'Intermediate',
          culturalNote: 'Essential for showing respect in Ethiopian culture',
          examples: ['እርስዎ እንዴት ነዎት?', 'በጣም ደህና ነዎት?'],
          emoji: '🙏'
        },
        {
          id: 'ante',
          amharic: 'አንተ',
          transliteration: 'ante',
          english: 'You (casual, masculine)',
          context: 'Informal address to friends',
          audio: 'ante',
          image: getImageUrl('pointing-he.jpg'),
          category: 'Casual Pronouns',
          difficulty: 'Beginner',
          culturalNote: 'Used with peers and close friends',
          examples: ['አንተ እንዴት ነህ?', 'አንተ ወዳጅዬ ነህ'],
          emoji: '👨‍👦'
        },
        {
          id: 'anchi',
          amharic: 'አንቺ',
          transliteration: 'anchi',
          english: 'You (casual, feminine)',
          context: 'Informal address to female friends',
          audio: 'anchi',
          image: getImageUrl('pointing-friend.jpg'),
          category: 'Casual Pronouns',
          difficulty: 'Beginner',
          culturalNote: 'Used with female peers and close friends.',
          examples: ['አንቺ ደህና ነሽ?', 'አንቺ ጎበዝ ነሽ'],
          emoji: '👩'
        },
        {
          id: 'ennesu',
          amharic: 'እነሱ',
          transliteration: 'ennesu',
          english: 'They',
          context: 'Third person plural',
          audio: 'ennesu',
          image: getImageUrl('group-they.jpg'),
          category: 'Plural Pronouns',
          difficulty: 'Beginner',
          culturalNote: 'Standard way to refer to a group of people.',
          examples: ['እነሱ ተማሪዎች ናቸው', 'እነሱ ይመጣሉ'],
          emoji: '👥'
        }
      ]
    },
    cultural: {
      title: 'Cultural Expressions',
      description: 'Phrases that capture Ethiopian cultural values',
      icon: FiHeart,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      items: [
        {
          id: 'amesegualehu',
          amharic: 'አመሰግናለሁ',
          transliteration: 'amesegualehu',
          english: 'Thank you',
          context: 'Expressing gratitude',
          audio: 'amesegualehu',
          image: getImageUrl('amesegualehu.jpg'),
          category: 'Gratitude',
          difficulty: 'Beginner',
          culturalNote: 'Essential for showing appreciation and respect',
          examples: ['እርስዎን አመሰግናለሁ ', 'በጣም አመሰግኖዎታለሁ'],
          emoji: '🙏'
        },
        {
          id: 'yikirta',
          amharic: 'ይቅርታ',
          transliteration: 'yikirta',
          english: 'Excuse me / Sorry',
          context: 'Politeness and apology',
          audio: 'yikirta',
          image: getImageUrl('yikirta.jpg'),
          category: 'Politeness',
          difficulty: 'Beginner',
          culturalNote: 'Important for maintaining social harmony',
          examples: ['ይቅር ይበሉኝ', 'በጣም ይቅርታ'],
          emoji: '🙏'
        },
        {
          id: 'tena-yistilign',
          amharic: 'ጤና ይስጥልኝ',
          transliteration: 'tena yistilign',
          english: 'Goodbye',
          context: 'Parting with respect',
          audio: 'tena-yistilign',
          image: getImageUrl('tena-yistilign.jpg'),
          category: 'Farewells',
          difficulty: 'Beginner',
          culturalNote: 'Polite way to end conversations ("May God give you health")',
          examples: ['መልካም ጤና ይስጥዎት', 'ጤና ይስጥልኝ'],
          emoji: '👋'
        },
        {
          id: 'betam',
          amharic: 'በጣም',
          transliteration: 'betam',
          english: 'Very much / Very',
          context: 'Intensifying expressions',
          audio: 'betam',
          image: getImageUrl('betam.jpg'),
          category: 'Intensifiers',
          difficulty: 'Beginner',
          culturalNote: 'Common intensifier in daily speech',
          examples: ['በጣም ደህና', 'በጣም ጥሩ'],
          emoji: '✨'
        },
        {
          id: 'eshi',
          amharic: 'እሺ',
          transliteration: 'eshi',
          english: 'Okay / Alright',
          context: 'Agreement and acknowledgement',
          audio: 'eshi',
          image: getImageUrl('eshi.jpg'),
          category: 'Agreement',
          difficulty: 'Beginner',
          culturalNote: 'Used constantly to show agreement, obedience, or just that you are listening.',
          examples: ['እሺ እመጣለሁ', 'እሺ በቃ'],
          emoji: '👌'
        },
        {
          id: 'abet',
          amharic: 'አቤት',
          transliteration: 'abet',
          english: 'Yes? / I am here',
          context: 'Response when called',
          audio: 'abet',
          image: getImageUrl('abet.jpg'),
          category: 'Response',
          difficulty: 'Beginner',
          culturalNote: 'A polite response when someone calls your name or knocks. Shows readiness to listen.',
          examples: ['አቤት ማን ልበል?', 'አቤት መጣሁ'],
          emoji: '👂'
        }
      ]
    },
    timeBased: {
      title: 'Time-Based Greetings',
      description: 'Greetings that change based on the time of day',
      icon: FiClock,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      items: [
        {
          id: 'early-morning',
          amharic: 'እንደምን አደርክ? / እንደምን አደርሽ?',
          transliteration: 'Endemn aderk? / adersh?',
          english: 'Good morning (Early)',
          context: 'Early Morning 🌅',
          audio: 'endemn-aderk',
          image: getImageUrl('endemn-aderk.jpg'),
          category: 'Morning',
          difficulty: 'Beginner',
          culturalNote: 'Literally "How was your night?". Used right after waking hours.',
          examples: ['እንደምን አደርክ? (to male)', 'እንደምን አደርሽ? (to female)'],
          emoji: '🌅'
        },
        {
          id: 'late-morning',
          amharic: 'ደህና አረፈዱ?',
          transliteration: 'Dehna arefedu?',
          english: 'Good morning (Late)',
          context: 'Late Morning ☀️',
          audio: 'dehna-arefedu',
          image: getImageUrl('dehna-arefedu.jpg'),
          category: 'Morning',
          difficulty: 'Intermediate',
          culturalNote: 'Polite check-in before noon. Literally "Did you rest well?".',
          examples: ['ደህና አረፈዱ?'],
          emoji: '☀️'
        },
        {
          id: 'afternoon',
          amharic: 'ደህና ዋልክ? / ደህና ዋልሽ?',
          transliteration: 'Dehna walk? / walsh?',
          english: 'Good afternoon',
          context: 'Noon / Afternoon 🌞',
          audio: 'dehna-walk',
          image: getImageUrl('dehna-walk.jpg'),
          category: 'Daytime',
          difficulty: 'Beginner',
          culturalNote: 'Literally "How is your day?". Used after midday.',
          examples: ['ደህና ዋልክ? (to male)', 'ደህና ዋሉ? (Polite)'],
          emoji: '🌞'
        },
        {
          id: 'evening',
          amharic: 'ደህና አመሸህ? / ደህና አመሸሽ?',
          transliteration: 'Dehna amesheh? / ameshesh?',
          english: 'Good evening',
          context: 'Evening 🌇',
          audio: 'dehna-amesheh',
          image: getImageUrl('dehna-amesheh.jpg'),
          category: 'Evening',
          difficulty: 'Beginner',
          culturalNote: 'Literally "How was your evening?".',
          examples: ['ደህና አመሸህ? (to male)', 'ደህና አመሹ? (Polite)'],
          emoji: '🌇'
        },
        {
          id: 'night',
          amharic: 'መልካም ሌሊት / ደህና እደር',
          transliteration: 'Melkam lelit / Dehna eder',
          english: 'Good night',
          context: 'Night 🌙',
          audio: 'melkam-lelit',
          image: getImageUrl('melkam-lelit.jpg'),
          category: 'Night',
          difficulty: 'Beginner',
          culturalNote: '"Have a good night" or "Sleep well".',
          examples: ['መልካም ሌሊት', 'ደህና እደር'],
          emoji: '🌙'
        }
      ]
    }
  };

  const currentCategory = audioCategories[activeCategory];

  const handlePlay = (track) => {
    // If clicking the same track
    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
      setIsPlaying(!isPlaying);
      return;
    }

    // If clicking a new track
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Determine URL: check if it's already a full URL or needs the base path
    const url = track.audio.startsWith('http') 
      ? track.audio 
      : getAudioUrl(track.audio);

    console.log("Playing audio from:", url);

    const audio = new Audio(url);
    
    // Set initial volume
    audio.volume = volume;
    audio.playbackRate = playbackSpeed;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });

    audio.addEventListener('timeupdate', () => {
      setProgress(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setProgress(0);
    });

    audio.addEventListener('error', (e) => {
      console.warn("Audio playback failed", e);
      setIsPlaying(false);
    });

    audioRef.current = audio;
    audio.play().catch(error => {
      console.error("Play failed:", error);
      setIsPlaying(false);
    });

    setCurrentTrack(track);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 5);
    }
  };

  const skipBack = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 5);
    }
  };

  const toggleFavorite = (trackId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(trackId)) {
      newFavorites.delete(trackId);
    } else {
      newFavorites.add(trackId);
    }
    setFavorites(newFavorites);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'Advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredItems = currentCategory.items.filter(item => 
    item.amharic.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.english.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.transliteration.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const AudioCard = ({ item, isPlaying: isTrackPlaying }) => (
    <div className={`bg-white rounded-[2rem] shadow-lg border-2 transition-all duration-300 overflow-hidden flex flex-col h-full p-4 ${currentTrack?.id === item.id ? 'border-purple-500 shadow-xl transform scale-105' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}`}>
      {/* Image Section - OPTIMIZED: Rounded container inside card with object-cover (removed padding/contain) */}
      <div className="relative h-48 rounded-2xl overflow-hidden bg-gray-50 border-2 border-gray-100 shadow-inner flex items-center justify-center mb-2">
        <img src={item.image} alt={item.english} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
        
        {/* Category Badge - Rounded Rectangle */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium shadow-sm border border-black/5 ${getDifficultyColor(item.difficulty)}`}>
            {item.difficulty}
          </span>
        </div>

        {/* Now Playing Badge */}
        {isTrackPlaying && (
          <div className="absolute top-3 right-3">
            <div className="bg-purple-500 text-white px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              Playing
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{item.emoji}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${currentCategory.bgColor} ${currentCategory.color.replace('from-', 'text-').split(' ')[0]}`}>
                {item.category}
              </span>
            </div>
            <h3 className="font-amharic text-2xl font-bold text-gray-800 mb-1">{item.amharic}</h3>
            <p className="text-lg text-gray-600 mb-1">{item.transliteration}</p>
            <p className="text-sm font-medium text-purple-600">{item.english}</p>
            <p className="text-xs text-gray-500 mt-2 italic">{item.context}</p>
          </div>
          <div className="flex flex-col gap-2">
            <button 
              onClick={() => toggleFavorite(item.id)}
              className={`p-2 rounded-xl transition-colors ${favorites.has(item.id) ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
            >
              <SafeIcon icon={FiHeart} className={favorites.has(item.id) ? 'fill-current' : ''} />
            </button>
            {/* Audio Icon Button - Activates Main Player */}
            <button 
              onClick={() => handlePlay(item)}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all shadow-sm border ${
                isTrackPlaying
                  ? 'bg-amber-500 text-white border-amber-600 scale-110'
                  : 'bg-white text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300'
              }`}
              title={isTrackPlaying ? "Pause Audio" : "Play Audio"}
            >
              <SafeIcon icon={isTrackPlaying ? FiPause : FiVolume2} className={isTrackPlaying ? 'animate-pulse' : ''} />
            </button>
          </div>
        </div>

        {/* Cultural Context */}
        <div className={`p-3 rounded-xl ${currentCategory.bgColor} border ${currentCategory.borderColor} mt-auto`}>
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiBook} className="text-purple-500 text-sm mt-0.5" />
            <div>
              <p className="text-xs text-gray-700 leading-relaxed">{item.culturalNote}</p>
            </div>
          </div>
        </div>

        {/* Examples */}
        {item.examples && (
          <div className="mt-3">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Examples</h4>
            <div className="space-y-1">
              {item.examples.slice(0, 2).map((example, idx) => (
                <div key={idx} className="text-xs bg-gray-50 rounded-lg px-2 py-1 text-gray-600 border border-gray-100">
                  <span className="font-amharic">{example}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Audio Gallery"
      subtitle="Immerse yourself in authentic Amharic pronunciation. Listen, learn, and practice with our curated audio collection."
      icon={FiHeadphones}
      color="text-purple-700"
      bgColor="bg-purple-50"
      prevLink="/exercises"
      prevLabel="Exercises"
      nextLink="/reflection"
      nextLabel="Quiz & Reflection"
    >
      <div className="space-y-8 fade-in">
        {/* Category Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap gap-3">
            {Object.entries(audioCategories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                  activeCategory === key
                    ? `bg-gradient-to-r ${category.color} text-white shadow-md transform scale-105`
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <SafeIcon icon={category.icon} />
                {category.title}
              </button>
            ))}
          </div>
        </div>

        {/* Category Overview */}
        <div className={`bg-gradient-to-r ${currentCategory.color} rounded-[2rem] p-8 text-white shadow-lg`}>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
              <SafeIcon icon={currentCategory.icon} className="text-2xl" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{currentCategory.title}</h2>
              <p className="text-white/90 leading-relaxed">{currentCategory.description}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{filteredItems.length}</div>
              <div className="text-sm text-white/70">Audio Items</div>
            </div>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Amharic phrases, English meanings..."
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-3 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-2">
                <SafeIcon icon={FiRepeat} /> Repeat All
              </button>
              <button className="px-4 py-3 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-2">
                <SafeIcon icon={FiDownload} /> Download All
              </button>
            </div>
          </div>
        </div>

        {/* Audio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <AudioCard key={item.id} item={item} isPlaying={currentTrack?.id === item.id && isPlaying} />
          ))}
        </div>

        {/* Learning Tips */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-[2rem] p-8 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-full">
              <SafeIcon icon={FiMic} className="text-blue-600 text-xl" />
            </div>
            <div>
              <h4 className="font-bold text-blue-900 mb-2">Pronunciation Learning Tips</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Listen multiple times to catch the rhythm</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Practice with slowed playback (0.75x speed)</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Record yourself to compare with native speakers</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Focus on the unique Amharic vowel sounds</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cultural Context Quote */}
        <div className="bg-gradient-to-r from-purple-700 to-indigo-700 rounded-[2rem] p-8 text-white text-center shadow-xl">
          <div className="max-w-3xl mx-auto">
            <SafeIcon icon={FiHeadphones} className="text-4xl text-yellow-400 mb-4" />
            <h3 className="text-2xl font-bold mb-4">Language is Music to the Ears</h3>
            <p className="text-purple-100 text-lg leading-relaxed mb-6">
              "In Ethiopian culture, the melody of language is as important as its meaning. Each phrase carries not just information, but emotion, respect, and cultural heritage."
            </p>
            <div className="flex items-center justify-center gap-2 text-purple-200">
              <SafeIcon icon={FiHeart} />
              <span className="italic">Listen with your heart, speak with respect</span>
              <SafeIcon icon={FiHeart} />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default AudioGallery;